import { Routes as RouterRoutes, Route } from "react-router-dom";
import { lazy } from "react";
import { LazyRoute } from "@/components/LazyRoute";

// Lazy load SEO landing pages (previously eager — moved to code-split bundles)
const DisneyWorldReviewsPage = lazy(() =>
  import("@/pages/SEOLandingPages").then((m) => ({ default: m.DisneyWorldReviewsPage }))
);
const DisneyChecklistsPage = lazy(() =>
  import("@/pages/SEOLandingPages").then((m) => ({ default: m.DisneyChecklistsPage }))
);
const DisneyTipsPage = lazy(() =>
  import("@/pages/seo/DisneyTipsPage").then((m) => ({ default: m.DisneyTipsPage }))
);
const DisneyPhotographyPage = lazy(() =>
  import("@/pages/seo/DisneyPhotographyPage").then((m) => ({ default: m.DisneyPhotographyPage }))
);
const DisneyPlanningPage = lazy(() =>
  import("@/pages/seo/DisneyPlanningPage").then((m) => ({ default: m.DisneyPlanningPage }))
);
const DisneyAttractionsPage = lazy(() =>
  import("@/pages/seo/DisneyAttractionsPage").then((m) => ({ default: m.DisneyAttractionsPage }))
);
const DisneyRestaurantsPage = lazy(() =>
  import("@/pages/seo/DisneyRestaurantsPage").then((m) => ({ default: m.DisneyRestaurantsPage }))
);
const DisneyEventsPage = lazy(() =>
  import("@/pages/seo/DisneyEventsPage").then((m) => ({ default: m.DisneyEventsPage }))
);

const DisneyWorldVsDisneyland = lazy(() => import("@/pages/seo/DisneyWorldVsDisneyland").then(m => ({default: m.DisneyWorldVsDisneyland})));
const DisneyWorldHotels = lazy(() => import("@/pages/seo/DisneyWorldHotels").then(m => ({default: m.DisneyWorldHotels})));
const DisneyWorldBudgetGuide = lazy(() => import("@/pages/seo/DisneyWorldBudgetGuide").then(m => ({default: m.DisneyWorldBudgetGuide})));
const DisneyWorldCrowdCalendar = lazy(() => import("@/pages/seo/DisneyWorldCrowdCalendar").then(m => ({default: m.DisneyWorldCrowdCalendar})));

// Lazy load all route components for better performance
const Index = lazy(() => import("@/pages/Index"));
const Videos = lazy(() => import("@/pages/Videos"));
const Feed = lazy(() => import("@/pages/Feed"));
const Profile = lazy(() => import("@/pages/Profile"));
const Search = lazy(() => import("@/pages/Search"));
const Messages = lazy(() => import("@/pages/Messages"));
const PostDetail = lazy(() => import("@/pages/PostDetail"));
const Groups = lazy(() => import("@/pages/Groups"));
const Group = lazy(() => import("@/pages/Group"));
const Events = lazy(() => import("@/pages/Events"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const Contests = lazy(() => import("@/pages/Contests"));
const ContestDetail = lazy(() => import("@/pages/ContestDetail"));
const Tips = lazy(() => import("@/pages/Tips"));
const Photographers = lazy(() => import("@/pages/Photographers"));
const Vloggers = lazy(() => import("@/pages/Vloggers"));
const Influencers = lazy(() => import("@/pages/Influencers"));
const TripPlanners = lazy(() => import("@/pages/TripPlanners"));
const Support = lazy(() => import("@/pages/Support"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const StyleGuide = lazy(() => import("@/pages/StyleGuide"));
const Admin = lazy(() => import("@/pages/Admin"));
const BulkDelete = lazy(() => import("@/pages/BulkDelete"));
const Moderation = lazy(() => import("@/pages/Moderation"));
const ActivitySummary = lazy(() => import("@/pages/ActivitySummary"));
const DailySummaryDetail = lazy(() => import("@/pages/DailySummaryDetail"));
const ChecklistDetailPage = lazy(() => import("@/pages/ChecklistDetail"));
const PublicChecklists = lazy(() => import("@/pages/PublicChecklists"));
const UserManagement = lazy(() => import("@/pages/UserManagement"));
const News = lazy(() => import("@/pages/News"));
const TripReports = lazy(() => import("@/pages/TripReports"));
const TripReportDetail = lazy(() => import("@/pages/TripReportDetail"));
const TripReportEditor = lazy(() => import("@/pages/TripReportEditor"));
const DisneyEntities = lazy(() => import("@/pages/DisneyEntities"));
const DisneyEntityDetail = lazy(() => import("@/pages/DisneyEntityDetail"));
const Hashtag = lazy(() => import("@/pages/Hashtag"));
const Bookmarks = lazy(() => import("@/pages/Bookmarks"));
const CreatorApply = lazy(() => import("@/pages/CreatorApply"));
const CreatorChannel = lazy(() => import("@/pages/CreatorChannel"));
const Creators = lazy(() => import("@/pages/Creators"));
const TravelAgents = lazy(() => import("@/pages/TravelAgents"));
const TravelAgentApply = lazy(() => import("@/pages/TravelAgentApply"));
const Trips = lazy(() => import("@/pages/Trips"));
const TripDetail = lazy(() => import("@/pages/TripDetail"));

export const Routes = () => {
  return (
    <RouterRoutes>
      <Route path="/" element={<LazyRoute><Index /></LazyRoute>} />
      <Route path="/feed" element={<LazyRoute><Feed /></LazyRoute>} />
      <Route path="/profile/:id" element={<LazyRoute><Profile /></LazyRoute>} />
      <Route path="/search" element={<LazyRoute><Search /></LazyRoute>} />
      <Route path="/messages" element={<LazyRoute><Messages /></LazyRoute>} />
      <Route path="/post/:id" element={<LazyRoute><PostDetail /></LazyRoute>} />
      <Route path="/groups" element={<LazyRoute><Groups /></LazyRoute>} />
      <Route path="/group/:id" element={<LazyRoute><Group /></LazyRoute>} />
      <Route path="/events" element={<LazyRoute><Events /></LazyRoute>} />
      <Route path="/reviews" element={<LazyRoute><Reviews /></LazyRoute>} />
      <Route path="/contests" element={<LazyRoute><Contests /></LazyRoute>} />
      <Route path="/contests/:id" element={<LazyRoute><ContestDetail /></LazyRoute>} />
      <Route path="/tips" element={<LazyRoute><Tips /></LazyRoute>} />
      <Route path="/photographers" element={<LazyRoute><Photographers /></LazyRoute>} />
      <Route path="/vloggers" element={<LazyRoute><Vloggers /></LazyRoute>} />
      <Route path="/influencers" element={<LazyRoute><Influencers /></LazyRoute>} />
      <Route path="/trip-planners" element={<LazyRoute><TripPlanners /></LazyRoute>} />
      <Route path="/support" element={<LazyRoute><Support /></LazyRoute>} />
      <Route path="/about" element={<LazyRoute><AboutUs /></LazyRoute>} />
      <Route path="/privacy" element={<LazyRoute><PrivacyPolicy /></LazyRoute>} />
      <Route path="/terms" element={<LazyRoute><TermsOfService /></LazyRoute>} />
      <Route path="/faq" element={<LazyRoute><FAQ /></LazyRoute>} />
      <Route path="/style-guide" element={<LazyRoute><StyleGuide /></LazyRoute>} />
      <Route path="/admin" element={<LazyRoute><Admin /></LazyRoute>} />
      <Route path="/admin/users" element={<LazyRoute><UserManagement /></LazyRoute>} />
      <Route path="/bulk-delete" element={<LazyRoute><BulkDelete /></LazyRoute>} />
      <Route path="/moderation" element={<LazyRoute><Moderation /></LazyRoute>} />
      <Route path="/activity" element={<LazyRoute><ActivitySummary /></LazyRoute>} />
      <Route path="/activity/:date" element={<LazyRoute><DailySummaryDetail /></LazyRoute>} />
      <Route path="/checklist/:id" element={<LazyRoute><ChecklistDetailPage /></LazyRoute>} />
      <Route path="/checklists/public" element={<LazyRoute><PublicChecklists /></LazyRoute>} />
      <Route path="/videos" element={<LazyRoute><Videos /></LazyRoute>} />
      <Route path="/news" element={<LazyRoute><News /></LazyRoute>} />
      <Route path="/trip-reports" element={<LazyRoute><TripReports /></LazyRoute>} />
      <Route path="/trip-reports/new" element={<LazyRoute><TripReportEditor /></LazyRoute>} />
      <Route path="/trip-reports/edit/:slug" element={<LazyRoute><TripReportEditor /></LazyRoute>} />
      <Route path="/trip-reports/:slug" element={<LazyRoute><TripReportDetail /></LazyRoute>} />
      <Route path="/disney" element={<LazyRoute><DisneyEntities /></LazyRoute>} />
      <Route path="/disney/:slug" element={<LazyRoute><DisneyEntityDetail /></LazyRoute>} />
      <Route path="/tag/:hashtag" element={<LazyRoute><Hashtag /></LazyRoute>} />
      <Route path="/bookmarks" element={<LazyRoute><Bookmarks /></LazyRoute>} />
      <Route path="/creator/apply" element={<LazyRoute><CreatorApply /></LazyRoute>} />
      <Route path="/c/:handle" element={<LazyRoute><CreatorChannel /></LazyRoute>} />
      <Route path="/creators" element={<LazyRoute><Creators /></LazyRoute>} />
      <Route path="/travel-agents" element={<LazyRoute><TravelAgents /></LazyRoute>} />
      <Route path="/travel-agents/apply" element={<LazyRoute><TravelAgentApply /></LazyRoute>} />
      <Route path="/trips" element={<LazyRoute><Trips /></LazyRoute>} />
      <Route path="/trips/:id" element={<LazyRoute><TripDetail /></LazyRoute>} />
      
      
      
      {/* SEO Landing Pages */}
      <Route path="/disney-world-reviews" element={<LazyRoute><DisneyWorldReviewsPage /></LazyRoute>} />
      <Route path="/disney-checklists" element={<LazyRoute><DisneyChecklistsPage /></LazyRoute>} />
      <Route path="/disney-world-tips" element={<LazyRoute><DisneyTipsPage /></LazyRoute>} />
      <Route path="/disney-photography" element={<LazyRoute><DisneyPhotographyPage /></LazyRoute>} />
      <Route path="/disney-trip-planning" element={<LazyRoute><DisneyPlanningPage /></LazyRoute>} />
      <Route path="/disney-attractions" element={<LazyRoute><DisneyAttractionsPage /></LazyRoute>} />
      <Route path="/disney-restaurants" element={<LazyRoute><DisneyRestaurantsPage /></LazyRoute>} />
      <Route path="/disney-events" element={<LazyRoute><DisneyEventsPage /></LazyRoute>} />
      
      {/* New SEO Pages */}
      <Route path="/disney-world-vs-disneyland" element={<LazyRoute><DisneyWorldVsDisneyland /></LazyRoute>} />
      <Route path="/disney-world-hotels" element={<LazyRoute><DisneyWorldHotels /></LazyRoute>} />
      <Route path="/disney-world-budget-guide" element={<LazyRoute><DisneyWorldBudgetGuide /></LazyRoute>} />
      <Route path="/disney-world-crowd-calendar" element={<LazyRoute><DisneyWorldCrowdCalendar /></LazyRoute>} />
    </RouterRoutes>
  );
};

export default Routes;